import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { SceneChip } from '@plotjack/core';
import { uvToScreen, type Uv } from '../art/anchors';
import type { FigureDef } from '../art/figures';
import { Canvas } from '@react-three/fiber';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { useTilt } from '../hooks/useTilt';
import { LayeredScene } from './three/LayeredScene';
import { overlayUrl } from '../art/assets';
import { getQuality } from '../art/quality';
import { audio } from '../audio/engine';

interface Props {
  sceneId: string;
  mood: string;
  glitch: boolean;
  overlayIds?: string[];
  /** changes on every card play so the fly-in, ripple and overlays replay */
  playKey?: string;
  playedCardId?: string;
  dread?: number;
  dramatic?: boolean;
  /** title screen hero */
  showpiece?: boolean;
  className?: string;
  /** where the played card lands (plate uv, top-left origin) */
  targetUv?: Uv;
  /** painted layer the target sits on, so the card meets it at the right depth */
  targetLayer?: number;
  /** name pin on the aimed or just-hit target; follows camera motion */
  pin?: { uv: Uv; label: string; hit: boolean; layer?: number };
  /** lasting changes pinned to the scene */
  chips?: SceneChip[];
  /** painted story figures for this story scene */
  figures?: FigureDef[];
}

export const SceneStage = memo(function SceneStage({ sceneId, mood, glitch, overlayIds, playKey, playedCardId, dread = 0, dramatic, showpiece, className, targetUv, targetLayer, pin, chips, figures }: Props) {
  const reduced = useReducedMotion();
  const boxRef = useRef<HTMLDivElement>(null);
  const [aspect, setAspect] = useState(1.6);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setAspect(e.contentRect.width / Math.max(1, e.contentRect.height)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const pinPos = pin ? uvToScreen(pin.uv, aspect) : null;
  const pinRef = useRef<HTMLDivElement>(null);
  const track = useMemo(
    () => (pin ? { uv: pin.uv, layer: pin.layer ?? 2, el: pinRef } : undefined),
    [pin?.uv[0], pin?.uv[1], pin?.layer], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const quality = useMemo(() => getQuality(), []);
  const { look } = useTilt(!reduced);
  const [impactKey, setImpactKey] = useState<string | undefined>();
  const missingOverlays = (overlayIds ?? []).filter((id) => !overlayUrl(id)).slice(0, 3);

  return (
    <div
      ref={boxRef}
      className={`scene-stage ${showpiece ? 'scene-stage--showpiece' : ''} ${className ?? ''}`}
      data-mood={mood}
      data-quality={quality.tier}
    >
      <Canvas
        className="scene-canvas"
        aria-hidden="true"
        camera={{ position: [0, 0, 0], fov: 38, near: 0.1, far: 60 }}
        dpr={quality.dpr}
        gl={{ antialias: !quality.post, alpha: false, powerPreference: 'high-performance' }}
        onCreated={({ gl }) => gl.setClearColor('#120d18')}
      >
        <LayeredScene
          sceneId={sceneId}
          mood={mood}
          dread={dread}
          reducedMotion={reduced}
          quality={quality}
          look={look}
          playKey={playKey}
          playedCardId={playedCardId}
          overlayIds={overlayIds}
          dramatic={dramatic}
          showpiece={showpiece}
          targetUv={targetUv}
          targetLayer={targetLayer}
          track={track}
          figures={figures}
          onImpact={() => {
            setImpactKey(playKey);
            audio.playSfx('card_land');
            if (glitch) window.setTimeout(() => audio.playSfx('glitch', { volume: 0.45 }), 180);
          }}
        />
      </Canvas>
      {missingOverlays.length > 0 && (
        <div className="scene-overlays" key={playKey} aria-hidden="true">
          {missingOverlays.map((id, i) => (
            <div key={id} className="scene-overlay-stamp" style={{ ['--i' as string]: i } as Record<string, number>}>
              {id.replace('overlay_', '').replace(/_/g, ' ')}
            </div>
          ))}
        </div>
      )}
      {glitch && impactKey === playKey && !reduced && <div className="scene-glitch-veil" key={`veil-${impactKey}`} aria-hidden="true" />}
      {pin && pinPos && (
        <div
          ref={pinRef}
          className={`scene-pin ${pin.hit ? 'scene-pin--hit' : ''}`}
          key={`${pin.label}-${pin.hit ? playKey : 'aim'}`}
          style={{ left: `${pinPos[0] * 100}%`, top: `${pinPos[1] * 100}%` }}
          aria-hidden="true"
        >
          <span className="scene-pin__ring" />
          <span className="scene-pin__label">{pin.label}</span>
        </div>
      )}
      {chips && chips.length > 0 && (
        <ul className="scene-chips" aria-label="Changes in this scene">
          {chips.map((c) => (
            <li key={`${c.elementId}-${c.chip}`} className={`scene-chip ${c.fromEarlier ? 'scene-chip--earlier' : ''}`} title={c.fromEarlier ? `Since ${c.sceneTitle}` : undefined}>
              <strong>{c.label}:</strong> {c.chip}
              {c.fromEarlier && <span className="scene-chip__since"> since {c.sceneTitle}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});
